import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { debounce, throttle } from 'lodash-es'

import { Keys } from '@/platform/platform'
import { valueInRange } from '@/utils/utils'

import { YouTubePlayerType } from './YouTubePlayer'

export const useRewindYouTubeVideo = (
  videoElementRef: RefObject<YouTubePlayerType>,
  currentTime: number,
  duration: number,
  setCurrentTime: Dispatch<SetStateAction<number>>,
  setPauseTimeUpdate: Dispatch<SetStateAction<boolean>>,
) => {
  const longPressCount = useRef(0)
  const seekPosition = useRef(currentTime)

  useEffect(() => {
    seekPosition.current = currentTime
  }, [currentTime])

  useEffect(() => {
    const cleanRewindData = () => {
      longPressCount.current = 0
    }
    window.addEventListener('keyup', cleanRewindData)

    return () => {
      window.removeEventListener('keyup', cleanRewindData)
    }
  }, [videoElementRef, longPressCount])

  const updateProgressBar = useCallback(
    (longPressCount: number, toForward: boolean) => {
      const targetTime = longPressCount <= 10 ? 10 : 30
      const newTime = toForward
        ? seekPosition.current + targetTime
        : seekPosition.current - targetTime

      const correctedTime = valueInRange(newTime, 0, duration)

      setCurrentTime(correctedTime)
      return correctedTime
    },
    [setCurrentTime, duration, seekPosition],
  )
  const seekToPosition = useCallback(
    debounce((newTime: number) => {
      videoElementRef.current?.seekTo(newTime, true)

      setTimeout(() => {
        setPauseTimeUpdate(false)
      }, 0)
    }, 700),
    [setPauseTimeUpdate, videoElementRef],
  )

  const seekBack = useCallback(
    throttle(() => {
      const newCount = longPressCount.current - 1
      longPressCount.current = newCount
      const newTime = updateProgressBar(newCount, false)

      seekToPosition(newTime)
    }, 500),
    [updateProgressBar, seekToPosition],
  )
  const seekForward = useCallback(
    throttle(() => {
      const newCount = longPressCount.current + 1
      longPressCount.current = newCount
      const newTime = updateProgressBar(newCount, true)

      seekToPosition(newTime)
    }, 500),
    [updateProgressBar, seekToPosition],
  )

  useEffect(() => {
    const keyPressHandler = (e: KeyboardEvent) => {
      if (e.keyCode === Keys.MEDIA_FAST_FORWARD) {
        seekForward()
        setPauseTimeUpdate(true)
      }
      if (e.keyCode === Keys.MEDIA_REWIND) {
        seekBack()
        setPauseTimeUpdate(true)
      }
    }

    window.addEventListener('keydown', keyPressHandler)
    return () => {
      window.removeEventListener('keydown', keyPressHandler)
    }
  }, [seekBack, seekForward, setPauseTimeUpdate])

  return {
    seekBack,
    seekForward,
  }
}
